/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import Table from "../ui/reports/Table";
import { useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import DatePicker from "../ui/reports/DatePicker";
import * as XLSX from "xlsx";

const Wrapper = styled.div`
  display: flex;
  flex-grow: 1;
  height: 100%;
  gap: 18px;
  padding: 18px;
  align-items: stretch;
  background:
    radial-gradient(circle at top left, rgba(47, 128, 237, 0.08), transparent 26%),
    linear-gradient(180deg, #f7f9fc 0%, #eef3f8 100%);

  @media (max-width: 1100px) {
    flex-direction: column-reverse;
    padding: 12px;
  }
`;

const padDatePart = (value) => String(value).padStart(2, "0");

const getMonthEndDate = (value) => {
  if (!value) return value;

  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${padDatePart(month)}-${padDatePart(lastDay)}`;
  }

  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return value;

  const year = parsed.getFullYear();
  const month = parsed.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${padDatePart(month)}-${padDatePart(lastDay)}`;
};


const getYesterdayDate = () => {
  const current = new Date();
  current.setDate(current.getDate() - 1);
  return current.toISOString().split("T")[0];
};
const applyMonthEndDate = (rows) =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row) {
      return row;
    }

    const copy = { ...row };

    Object.keys(copy).forEach((key) => {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey === "transaction_date" || normalizedKey === "date") {
        copy[key] = getMonthEndDate(copy[key]);
      }
    });

    return copy;
  });

const getResponseResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};


const formatValidationAmount = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getValidationTotals = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (totals, row) => ({
      debit: totals.debit + Number(row?.debit || 0),
      credit: totals.credit + Number(row?.credit || 0),
      diff: totals.diff + Number(row?.diff || 0),
    }),
    { debit: 0, credit: 0, diff: 0 }
  );
const Reports = () => {
  const [data, setData] = useState([]); // state for fetched data
  const [reportColumns, setReportColumns] = useState(null); // state for report columns
  const [reportSum, setReportSum] = useState(null); // state for report sum (vals)
  const [reportCols, setReportCols] = useState([]); // state for report columns (cols)
  //const [reportCols, setReportCols] = useState(null); // state for report columns (cols)
  const tableRef = useRef();
  const [selectedReportId, setSelectedReportId] = useState(null);  
  const [selectedReportName, setSelectedReportName] = useState(""); 
  const [analyticsAutoSaveTrigger, setAnalyticsAutoSaveTrigger] = useState(0);

  const [fromDate, setFromDate] = useState(getYesterdayDate());  // state for fromDate
  const [toDate, setToDate] = useState(getYesterdayDate());      // state for toDate
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationResults, setValidationResults] = useState([]);
  const [showValidationDifferencesOnly, setShowValidationDifferencesOnly] = useState(true);

  //was:
  //const rows = ["transaction_date", "account_description", "debit", "credit"];
  
  // default values
  const defaultRows = ["transaction_date", "account_description", "debit", "credit"]; // default rows when no Report selected
  const defaultVals = ["debit_credit_amount"];  // Default value for vals
  //const cols = [];  // default cols
  const defaultCols = [];
  const aggregatorName = "Sum";  // default aggregator
  const vals_sort = ["desc"];  // sort direction
  const rendererName = "Table";  // renderer

  // Get user_id, company_id from Redux
  const userState = useSelector((state) => state.userState);
  const userId = userState.user.user_id;
  const companyId = useSelector((state) => state.database.currentCompany.id);
  const shopId = useSelector((state) => state.database.currentShop.shop_id);
  const companyType = useSelector((state) => state.database.currentCompany.type); // company type (RETAIL, RESTO, etc)
  const areaId = useSelector((state) => state.database.currentShop.area_id);

  const currentTab = useSelector((state) => state.ui.table); // Track the current tab
  const reportScopeKey = `${companyType || ""}-${companyId || ""}-${shopId || ""}-${areaId || ""}`;
  const activeReportScope = useRef(reportScopeKey);
  const reportRequestId = useRef(0);
  activeReportScope.current = reportScopeKey;
  const datePickerScopeKey =
    companyType === "RESTOK"
      ? `${companyType || ""}-${companyId || ""}`
      : reportScopeKey;

  // Sync logic
  const isReportRetailTab = currentTab === 6; // Report Retail corresponds to tab 6
  const isRetailReport = isReportRetailTab && (companyType === "RETAIL" || companyType === "RETAILX");
  const isRestaurantKReport = currentTab === 10 && companyType === "RESTOK";
  const showReportValidation = isRetailReport || isRestaurantKReport;

  // Log userId, companyId, and shopId
  useEffect(() => {
    //console.log("User ID:", userId, "Company ID:", companyId, "Shop ID:", shopId);
  }, [userId, companyId, shopId]);

  useEffect(() => {
    console.log("Rendering Reports Component for RESTO");
  }, []);

  useEffect(() => {
    setData([]);
  }, [reportScopeKey]);

  useEffect(() => {
    setReportColumns(null);
    setReportSum(null);
    setReportCols([]);
    setSelectedReportId(null);
    setSelectedReportName("");
  }, [datePickerScopeKey]);

  useEffect(() => () => {
    reportRequestId.current += 1;
  }, []);


  // API endpoint based on the company type (Retail or Restaurant)
  const getApiUrl = () => {
    console.log("REPORT companyType ->> ", companyType);
    if (companyType === "RESTOK") {
      return "https://reportapi.beesql.com/api/admin/restok/report";
    } else if (companyType === "RESTO" || companyType === "RESTOL") {
      return "https://reportapi.beesql.com/api/admin/restol/report";
    } else if (companyType === "RETAILX") {
      return "https://reportapi.beesql.com/api/admin/retailx/report";
    } else {
      return "https://reportapi.beesql.com/api/admin/retailr/report";
    }
  };

  // fetch data from API
  const fetchData = async (fromDate, toDate, reportName, reportId, showDateAsMonthEnd = false, layoutRows, layoutCols) => {
    const requestId = ++reportRequestId.current;
    const requestScope = reportScopeKey;
    console.log("Fetching data with reportName:", reportName, "and reportId:", reportId); 
    
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
  
    const columnsToSend = Array.isArray(layoutRows)
      ? layoutRows
      : reportColumns && reportColumns.length > 0 ? reportColumns : defaultRows;
    // cols_fields no have empty strings or null values
    const filteredCols = (Array.isArray(layoutCols) ? layoutCols : reportCols)
      .filter(col => col !== "" && col !== null);

  
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({
        token_user_id: userId,
        company_id: companyId,
        shop_id: companyType === 'RESTOK' || companyType === 'RESTO' ? areaId : shopId,
        register_id: 99,
        from_date: fromDate,
        to_date: toDate,
        xoption: "",
        report_id: reportId || "" || selectedReportId  , 
        report_name: reportName || selectedReportName || "" ,  
        show_date_as_month_end: showDateAsMonthEnd ? "on" : "off",
        selected_columns: columnsToSend,
        cols_fields: filteredCols.length > 0 ? filteredCols : defaultCols,
      }),
    };
  
    try {
      const response = await fetch(getApiUrl(), requestOptions);
      const data = await response.json();
      if (requestId !== reportRequestId.current || requestScope !== activeReportScope.current) return;
      const results = getResponseResults(data);
      setData(showDateAsMonthEnd ? applyMonthEndDate(results) : results);
    } catch (error) {
      console.error(error);
    }
  };


  const handleValidateReport = async () => {
    setValidationOpen(true);
    setValidationLoading(true);
    setValidationError("");
    setValidationResults([]);
    setShowValidationDifferencesOnly(true);

    const validationFromDate = fromDate || "2023-01-01";
    const validationToDate = toDate || "2023-12-31";
    const columnsToSend = reportColumns && reportColumns.length > 0 ? reportColumns : defaultRows;
    const filteredCols = reportCols.filter((col) => col !== "" && col !== null);

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({
        token_user_id: userId,
        company_id: companyId,
        shop_id: companyType === "RESTOK" || companyType === "RESTO" ? areaId : shopId,
        register_id: 99,
        from_date: validationFromDate,
        to_date: validationToDate,
        xoption: "",
        report_id: selectedReportId || "",
        report_name: "default",
        show_date_as_month_end: "off",
        selected_columns: columnsToSend,
        cols_fields: filteredCols.length > 0 ? filteredCols : defaultCols,
        validation: 1,
      }),
    };

    try {
      const response = await fetch(getApiUrl(), requestOptions);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Validation request failed");
      }
      setValidationResults(getResponseResults(data));
    } catch (error) {
      console.error(error);
      setValidationError(error?.message || "Validation request failed");
    } finally {
      setValidationLoading(false);
    }
  };
  // Handle save report to update the persisted reportId and reportName
  const handleSaveReport = (reportId, reportName) => {
    console.log("Report saved - ID:", reportId, "Name:", reportName); // Log the report details
    setSelectedReportId(reportId);  // saved report ID
    setSelectedReportName(reportName);  // saved report name
  };

  const handleExportValidationToExcel = () => {
    const exportData = validationResults.map((row) => ({
      Date: row?.transaction_date || "",
      "Sale ID": row?.sale_id || "",
      Debit: Number(row?.debit || 0),
      Credit: Number(row?.credit || 0),
      Diff: Number(row?.diff || 0),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Validation");
    const validationFilePrefix = isRestaurantKReport
      ? "restaurant-k-report-validation"
      : "retail-report-validation";
    XLSX.writeFile(workbook, `${validationFilePrefix}-${fromDate || "2023-01-01"}-${toDate || "2023-12-31"}.xlsx`);
  };
  const visibleValidationResults = showValidationDifferencesOnly
    ? validationResults.filter((row) => Math.abs(Number(row?.diff || 0)) > 0.005)
    : validationResults;
  const validationTotals = getValidationTotals(visibleValidationResults);

  return (
    <Wrapper>
      <Table
        key={`table-${reportScopeKey}`}
        ref={tableRef}
        data={data}
        // was: rows={rows}
        rows={reportColumns && reportColumns.length > 0 ? reportColumns : defaultRows} // defualt or columns accordig to selected Report
        //cols={reportCols && reportCols.length > 1 ? reportCols : cols}
        cols={reportCols && reportCols.length > 0 ? reportCols : defaultCols} 
        aggregatorName={aggregatorName}
        //vals={vals}
        //vals={reportSum && reportSum.length > 1 ? reportSum : vals}
        vals={reportSum && reportSum.length > 0 ? reportSum : defaultVals} 
        sortDirection={vals_sort}
        rendererName={rendererName}
        reportName={selectedReportName}
        onAnalyticsFieldDrop={() => setAnalyticsAutoSaveTrigger((value) => value + 1)}
        showReportInfoIcon={showReportValidation}
        onReportInfoClick={handleValidateReport}
      />
      <DatePicker 
        key={`datepicker-${datePickerScopeKey}`}
        updateAction={(fromDate, toDate, reportName, reportId, showDateAsMonthEnd, layoutRows, layoutCols) => {
          setFromDate(fromDate);
          setToDate(toDate);
          return fetchData(
            fromDate,
            toDate,
            reportName || selectedReportName,
            reportId || selectedReportId,
            showDateAsMonthEnd,
            layoutRows,
            layoutCols
          );
        }} // latest state for reportName and reportId
        tableRef={tableRef}
        userId={userId}  // pass userId to DatePicker which contains select list where it's used
        companyId={companyId}  // pass companyId to DatePicker
        companyType={companyType}
        autoSyncOnLoad={isRetailReport}
        analyticsAutoSaveTrigger={analyticsAutoSaveTrigger}
        fromDate={fromDate}  // from date state
        toDate={toDate}  // to date state
        onDateChange={(nextFromDate, nextToDate) => {
          setFromDate(nextFromDate);
          setToDate(nextToDate);
        }}
        onReportChange={(columns, reportId, reportName, sum_field, cols_fields) => {
          setReportColumns(columns);
          setReportSum(sum_field);  // save sum_field for vals 
            // filter out empty or null values from cols_fields 
          const filteredColsFields = cols_fields.filter(col => col !== "" && col !== null);

          setReportCols(filteredColsFields);
          setSelectedReportId(reportId);  // selected report_id (id)
          setSelectedReportName(reportName);  // selected report_name
        }}
        onSaveReport={(reportId, reportName) => handleSaveReport(reportId, reportName)}  // persist report after save
      />
      {validationOpen && (
        <div
          onClick={() => !validationLoading && setValidationOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10020,
            background: "rgba(15, 23, 42, 0.52)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "min(760px, calc(100vw - 48px))",
              maxHeight: "calc(100vh - 48px)",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 24px 80px rgba(15, 23, 42, 0.28)",
              border: "1px solid #d8e2f0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "18px 22px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                  {isRestaurantKReport ? "Restaurant (K) Report Validation" : "Retail Report Validation"}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>
                  {fromDate || "2023-01-01"} - {toDate || "2023-12-31"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setValidationOpen(false)}
                disabled={validationLoading}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#64748b",
                  cursor: validationLoading ? "default" : "pointer",
                  fontSize: 24,
                  lineHeight: 1,
                }}
                aria-label="Close validation"
              >
                &times;
              </button>
            </div>

            <div style={{ padding: 22, overflowY: "auto" }}>
              {validationLoading && (
                <div style={{ padding: 24, textAlign: "center", color: "#475569", fontWeight: 700 }}>
                  Validating report...
                </div>
              )}

              {!validationLoading && validationError && (
                <div style={{ padding: 14, borderRadius: 8, background: "#fef2f2", color: "#991b1b", fontWeight: 700 }}>
                  {validationError}
                </div>
              )}

              {!validationLoading && !validationError && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10, marginBottom: 16 }}>
                    {[
                      ["Debit", validationTotals.debit],
                      ["Credit", validationTotals.credit],
                      ["Diff", validationTotals.diff],
                    ].map(([label, value]) => (
                      <div key={label} style={{ border: "1px solid #dbe5f2", borderRadius: 8, padding: 12, background: "#f8fafc" }}>
                        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>{label}</div>
                        <div style={{ marginTop: 4, fontSize: 18, color: Math.abs(Number(value || 0)) > 0.005 && label === "Diff" ? "#b91c1c" : "#0f172a", fontWeight: 900 }}>
                          {formatValidationAmount(value)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#334155", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={showValidationDifferencesOnly}
                        onChange={(event) => setShowValidationDifferencesOnly(event.target.checked)}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                      Show differences only
                    </label>
                    <button
                      type="button"
                      onClick={handleExportValidationToExcel}
                      disabled={validationResults.length === 0}
                      style={{
                        border: "1px solid #2563eb",
                        background: validationResults.length === 0 ? "#e2e8f0" : "#2563eb",
                        color: validationResults.length === 0 ? "#64748b" : "#fff",
                        cursor: validationResults.length === 0 ? "default" : "pointer",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      Export to Excel
                    </button>
                  </div>
                  {validationResults.length === 0 ? (
                    <div style={{ padding: 18, borderRadius: 8, background: "#f8fafc", color: "#475569", textAlign: "center", fontWeight: 700 }}>
                      No validation results returned.
                    </div>
                  ) : visibleValidationResults.length === 0 ? (
                    <div style={{ padding: 18, borderRadius: 8, background: "#f8fafc", color: "#475569", textAlign: "center", fontWeight: 700 }}>
                      No differences found.
                    </div>
                  ) : (
                    <div style={{ overflow: "auto", maxHeight: "min(55vh, 520px)", border: "1px solid #dbe5f2", borderRadius: 8 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: "#edf4fc", color: "#0f172a" }}>
                            <th style={{ position: "sticky", top: 0, zIndex: 1, background: "#edf4fc", padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #dbe5f2" }}>Date</th>
                            <th style={{ position: "sticky", top: 0, zIndex: 1, background: "#edf4fc", padding: "10px 12px", textAlign: "left", borderBottom: "1px solid #dbe5f2" }}>Sale ID</th>
                            <th style={{ position: "sticky", top: 0, zIndex: 1, background: "#edf4fc", padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #dbe5f2" }}>Debit</th>
                            <th style={{ position: "sticky", top: 0, zIndex: 1, background: "#edf4fc", padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #dbe5f2" }}>Credit</th>
                            <th style={{ position: "sticky", top: 0, zIndex: 1, background: "#edf4fc", padding: "10px 12px", textAlign: "right", borderBottom: "1px solid #dbe5f2" }}>Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleValidationResults.map((row, index) => {
                            const diff = Number(row?.diff || 0);
                            return (
                              <tr key={`${row?.transaction_date || "date"}-${row?.sale_id || "total"}-${index}`} style={{ background: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                <td style={{ padding: "9px 12px", borderBottom: "1px solid #edf2f7", color: "#0f172a", fontWeight: 700 }}>{row?.transaction_date || ""}</td>
                                <td style={{ padding: "9px 12px", borderBottom: "1px solid #edf2f7", color: "#334155" }}>
                                  {row?.sale_id ? (
                                    <a
                                      href={`https://us.merchantos.com/?name=transaction.views.transaction&form_name=view&id=${encodeURIComponent(row.sale_id)}&tab=details`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: "#2563eb", fontWeight: 700, textDecoration: "underline" }}
                                    >
                                      {row.sale_id}
                                    </a>
                                  ) : ""}
                                </td>
                                <td style={{ padding: "9px 12px", borderBottom: "1px solid #edf2f7", textAlign: "right", fontWeight: 700 }}>{formatValidationAmount(row?.debit)}</td>
                                <td style={{ padding: "9px 12px", borderBottom: "1px solid #edf2f7", textAlign: "right", fontWeight: 700 }}>{formatValidationAmount(row?.credit)}</td>
                                <td style={{ padding: "9px 12px", borderBottom: "1px solid #edf2f7", textAlign: "right", color: Math.abs(diff) > 0.005 ? "#b91c1c" : "#047857", fontWeight: 900 }}>{formatValidationAmount(diff)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default Reports;
